import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button, showToast, NewTableAdv, Modal, DocumentsList, CustomSelect } from "../../../../components";
import { exactUTCDate } from "../../../../utils/dateFormator";
import { mapTableRows } from "../../../../utils";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setEmployeeProfileUiid } from "@/redux/slices/employee/employeeSlice";
import { addCurrentPlacement } from "@/redux/slices/placements/placementsSlice";
import { fetchUsers, fetchIncentives, fetchOneIncentive, fetchIncentiveSettings, updateIncentive } from "@/redux/slices/incentives/incentivesApi";
import axiosRequest from "@/redux/slices/universal/axiosService";
import { ENDPOINTS } from "@/redux/apiConfig";

import InfoText from "../../../../components/Info/InfoText";
import InfoWrapper from "@/components/Info/InfoWrapper";
import FormFieldSet from "@/components/FormFields/FormFieldSet";
import Placements from "@/pages/InnerPages/Employee/EmployeeProfile/Placements";
import { dateFormator } from "@/utils/dateFormator";
import PlacementDetailCard from "./components/PlacementDetailCard";
import EditSectionModal from "./components/EditSectionModal";
import { EMPLOYEE_FIELDS, CLIENT_FIELDS, PLACEMENT_FIELDS } from "./components/fields";

/* ─── Helpers for nested value access ─── */
const getNestedValue = (obj, path) =>
  path.split(".").reduce((acc, part) => acc?.[part], obj);

const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  const clone = { ...obj };
  let ref = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    ref[keys[i]] = { ...ref[keys[i]] };
    ref = ref[keys[i]];
  }
  ref[keys[keys.length - 1]] = value;
  return clone;
};

const chunkArray = (arr, size) => {
  const chunked = [];
  for (let i = 0; i < arr.length; i += size) {
    chunked.push(arr.slice(i, i + size));
  }
  return chunked;
};

/* ─── Format display values for read-only ─── */
const CLASSIFICATION_MAP = { 1: "Billable", 2: "Non-Billable" };
const EMPLOYMENT_TYPE_MAP = { 1: "Regular", 2: "Permanent", 3: "Contractor", 4: "Reference" };
const GENDER_MAP = { 1: "Male", 2: "Female", 3: "Wish not to specify", 4: "Other" };
const PLACEMENT_TYPE_MAP = { 1: "Contract", 2: "Permanent" };
const COMMISSION_TYPE_MAP = { 1: "Flat", 2: "Percentage" };
const AGREEMENT_NET_TERMS_MAP = { 1: "Net 30", 2: "Net 60", 3: "Net 90", 4: "Split(50-50)", 5: "Split(25-25-25-25)", 6: "Split(3 Equal Parts)" };
const WARRANTY_MAP = { 1: "30 days", 2: "60 days", 3: "90 days", 4: "120 days" };
const BILL_TYPE_MAP = { 1: "Hourly", 2: "Daily", 3: "Weekly", 4: "Monthly", 5: "Annual" };

const safeDateFormat = (value) => {
  if (!value || value === "-" || value === "Invalid Date") return "-";
  if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) return value;
  return dateFormator(value, "date-month-year");
};

const parsePhoneString = (phone) => {
  if (!phone || phone === "-") return { code: "+1", countryCode: "+1", number: "" };
  
  // If it's already a structured object, return it (but ensure toString exists)
  if (typeof phone === "object") {
    const rawNumber = phone.number || "";
    // If phone.number is itself an object (due to a previous bug), flatten it
    const finalNumber = typeof rawNumber === "object" ? (rawNumber.number || "") : rawNumber;
    const res = { 
      code: phone.code || phone.countryCode || (typeof rawNumber === "object" ? rawNumber.code : "+1"),
      countryCode: phone.countryCode || phone.code || (typeof rawNumber === "object" ? rawNumber.countryCode : "+1"),
      number: finalNumber 
    };
    res.toString = function() { return this.number || ""; };
    return res;
  }

  // Handle strings like +919391786789 or standard 10 digits
  let clean = phone.trim();
  if (!clean.startsWith("+")) {
    clean = clean.length === 10 ? `+1${clean}` : `+${clean}`;
  }

  // Basic split for common country codes (1-3 digits)
  const match = clean.match(/^(\+1|\+91|\+92|\+44|\+61)(\d+)$/) || clean.match(/^(\+\d{1,3})(\d+)$/);

  const result = match
    ? { code: match[1], countryCode: match[1], number: match[2] }
    : { code: "+1", countryCode: "+1", number: clean.replace(/^\+1/, "") };
  return result;
};

const formatFieldValue = (key, value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (["startDate", "endDate", "dob"].includes(key)) {
    return safeDateFormat(value);
  }
  if (["offeredSalary", "commissionValue", "commission", "billRate", "payRate"].includes(key)) {
    if (value === "-") return "-";
    const cleaned = String(value).replace(/[$,]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? "-" : `$${num.toLocaleString()}`;
  }
  if (["percentage", "targetPercentage"].includes(key)) {
    if (value === "-") return "-";
    const num = parseFloat(value);
    return isNaN(num) ? "-" : `${num}%`;
  }
  if (key === "classification") return CLASSIFICATION_MAP[Number(value)] || value;
  if (key === "employmentType") return EMPLOYMENT_TYPE_MAP[Number(value)] || value;
  if (key === "gender") return GENDER_MAP[Number(value)] || value;
  if (key === "placementType") return PLACEMENT_TYPE_MAP[Number(value)] || value;
  if (key === "commissionType") return COMMISSION_TYPE_MAP[Number(value)] || value;
  if (key === "netTerms") return AGREEMENT_NET_TERMS_MAP[Number(value)] || value;
  if (key === "warrantyPeriod") return WARRANTY_MAP[Number(value)] || value;
  if (key === "billType") return BILL_TYPE_MAP[Number(value)] || value;
  if (typeof value === "object" && value !== null && "number" in value) {
    return `${value.countryCode || value.code || ""} ${value.number}`.trim() || "-";
  }
  if (typeof value === "object" && value !== null && ("label" in value || "value" in value)) {
    return value.label || value.value || "-";
  }
  if (typeof value === "object" && value !== null) {
    return String(value) || "-";
  }
  return value;
};

/* ─── Read-only row using a grid for better alignment ─── */
const ReadOnlyRow = ({ fields, cols = "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" }) => (
  <div className={`grid ${cols} gap-x-12 gap-y-6`}>
    {fields.map(({ label, value }, i) => {
      let displayValue = value || "-";
      if (typeof value === "object" && value !== null && "number" in value) {
        const code = value.countryCode || value.code || "";
        const num = value.number || "";
        displayValue = (code && num) ? `${code} ${num}`.trim() : (num || code || "-");
      } else if (typeof value === "string" && value !== "-") {
        displayValue = value;
      } else if (typeof value === "object" && value !== null && ("label" in value || "value" in value)) {
        displayValue = value.label || value.value || "-";
      } else if (typeof value === "object" && value !== null) {
        displayValue = String(value) || "-";
      }
      return <InfoText key={i} title={label} value={displayValue} size="sm" textWrap />;
    })}
  </div>
);

/* ────────────── Main Component ────────────── */
const IncomingPlacementDetails = ({ placementData, onBack }) => {
  const { employee, client, placement, team, documents, organizationId } = placementData;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [documentModal, setDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [atsUsers, setAtsUsers] = useState([]);
  const [loadingIncentives, setLoadingIncentives] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [approving, setApproving] = useState(false);
  const [checkingExistence, setCheckingExistence] = useState(false);
  const [existence, setExistence] = useState({
    employee: false,
    client: false,
    placement: false
  });
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [originalTeamData, setOriginalTeamData] = useState(null);
  const isProcessed = placementData?.status === 'Processed';

  const [editingSection, setEditingSection] = useState(null); // "employee" | "client" | "placement" | null
  const [empData, setEmpData] = useState(() => {
    const extractString = (val) => val?.label ?? val?.value ?? val ?? "-";
    return {
      ...employee,
      // Standardize to keys used by AddEmployee.jsx and fields.js
      emailAddress: employee.emailAddress || employee.email || "",
      alternateEmailAddress: employee.alternateEmailAddress || employee.alternateEmail || "",
      contactNumber: parsePhoneString(employee.contactNumber || employee.phone || ""),
      address: {
        ...employee?.address,
        city: extractString(employee?.address?.city),
        state: extractString(employee?.address?.state),
        country: extractString(employee?.address?.country) || "United States",
      },
      startDate: employee.startDate || employee.joiningDate || placement?.startDate || ""
    };
  });
  const [clientData, setClientData] = useState(() => {
    const extractString = (val) => val?.label ?? val?.value ?? val ?? "-";
    const c = {
      ...client,
      emailAddress: client.emailAddress || client.email || "",
      contactNumber: parsePhoneString(client.contactNumber || client.phone || ""),
      address: {
        ...client?.address,
        city: extractString(client?.address?.city),
        state: extractString(client?.address?.state),
        country: extractString(client?.address?.country) || "United States",
      }
    };
    if (c.poc?.phone) c.poc.phone = parsePhoneString(c.poc.phone);
    if (c.billingPOC?.phone) c.billingPOC.phone = parsePhoneString(c.billingPOC.phone);
    return c;
  });
  const [placeData, setPlaceData] = useState(() => {
    const p = placementData?.placement || {};
    const billRate = Number(p.billRate) || 0;
    const payRate = Number(p.payRate) || 0;
    
    const pType = Number(p.placementType || (placementData.module === "IT" ? 1 : (placementData.module === "NonIT" ? 2 : (placementData.module === "Bench" ? 3 : 2))));
    const commType = Number(p.commissionType || 2);
    const targetPercentage = Number(p.targetPercentage || p.percentage) || 0;
    
    let finalOfferedSalary = Number(p.offeredSalary || p.offerSalary) || 0;
    let finalTargetAmount = Number(p.targetAmount) || 0;

    if (pType === 1) {
      // Contract (IT) Logic
      finalOfferedSalary = payRate;
      finalTargetAmount = billRate - payRate;
    } else if (pType === 2) {
      // Permanent (Non-IT) Logic: Sync Bill Rate and Offered Salary
      const base = Number(p.offeredSalary || p.offerSalary || billRate) || 0;
      finalOfferedSalary = base;
      if (commType === 1) {
        // Flat
        finalTargetAmount = Number(p.targetAmount) || 0;
      } else {
        // Percentage
        finalTargetAmount = Math.round(base * (targetPercentage / 100));
      }
    }

    return { 
      ...p,
      startDate: p.startDate && p.startDate !== "-" ? safeDateFormat(p.startDate) : "",
      billType: p.billType || "-",
      billRate: pType === 2 ? finalOfferedSalary : billRate,
      payRate,
      offeredSalary: finalOfferedSalary,
      targetPercentage: (pType === 1 && billRate > 0) ? Math.round(((billRate - payRate) / billRate) * 100 * 100) / 100 : 
                       (pType === 2 && commType === 1 && finalOfferedSalary > 0) ? Math.round((finalTargetAmount / finalOfferedSalary) * 100 * 100) / 100 : targetPercentage,
      targetAmount: finalTargetAmount,
      commissionType: commType, // Default to Percentage
      team: team || [] 
    };
  });

  /* ─── Check Existence of records in Workforce ─── */
  useEffect(() => {
    const checkExistence = async () => {
      const results = { employee: false, client: false, placement: false };
      
      try {
        setCheckingExistence(true);
        const checks = [];

        if (placementData?.wfEmployeeUiid) {
          checks.push(
            axiosRequest({
              method: "GET",
              url: ENDPOINTS.EMPLOYEES.GET_BY_UIID,
              params: { 
                coll: "Employee", 
                fil: JSON.stringify({ uiid: { type: "NONE", value: placementData.wfEmployeeUiid } }) 
              }
            }).then(res => { 
              console.log("[ExistenceCheck] Employee:", placementData.wfEmployeeUiid, !!(res?.success && res.data));
              if (res?.success && res.data) results.employee = true; 
            })
          );
        }

        if (placementData?.wfClientUiid) {
          checks.push(
            axiosRequest({
              method: "GET",
              url: ENDPOINTS.CLIENTS.GET_BY_UIID,
              params: { 
                coll: "Client", 
                fil: JSON.stringify({ uiid: { type: "NONE", value: placementData.wfClientUiid } }) 
              }
            }).then(res => { 
              console.log("[ExistenceCheck] Client:", placementData.wfClientUiid, !!(res?.success && res.data));
              if (res?.success && res.data) results.client = true; 
            })
          );
        }

        if (placementData?.wfPlacementUiid) {
          checks.push(
            axiosRequest({
              method: "GET",
              url: ENDPOINTS.PLACEMENTS.GET_BY_UIID,
              params: { 
                coll: "Placement", 
                fil: JSON.stringify({ uiid: { type: "NONE", value: placementData.wfPlacementUiid } }) 
              }
            }).then(res => { 
              console.log("[ExistenceCheck] Placement:", placementData.wfPlacementUiid, !!(res?.success && res.data));
              if (res?.success && res.data) results.placement = true; 
            })
          );
        }

        if (checks.length > 0) {
          await Promise.allSettled(checks);
        }
      } catch (err) {
        console.error("Error checking existence:", err);
      } finally {
        setExistence(results);
        setCheckingExistence(false);
      }
    };

    checkExistence();
  }, [placementData?.wfEmployeeUiid, placementData?.wfClientUiid, placementData?.wfPlacementUiid]);

  /* ─── Saved indicator badge ─── */
  const SavedBadge = () => (
    <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      Created
    </span>
  );

  // Track which sections have been created in Workforce
  // We use the existence check result, but default to true if the check is still pending or failed but ID is present
  // to avoid flicker/hiding badges while loading details.
  const savedSections = {
    employee: !!placementData?.wfEmployeeUiid && existence.employee,
    client: !!placementData?.wfClientUiid && existence.client,
    placement: !!placementData?.wfPlacementUiid && existence.placement
  };

  const isReadyToApprove = !!(savedSections.employee && savedSections.client && savedSections.placement);

  const handleSaveTeam = async () => {
    try {
      setSavingTeam(true);

      const isBench = placementData?.module === "BenchSales" || placementData?.module === "Bench";
      const effectiveModule = isBench ? placementData.module : (placeData?.placementType === 1 ? "IT" : "NonIT");

      const res = await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.INCOMING_PLACEMENTS.UPDATE,
        params: { coll: "IncomingPlacement", uiid: placementData.uiid },
        data: { payload: { updateFields: { type: "SET", value: { team: placeData.team, module: effectiveModule, _skipFlatten: ["team"] } } } },
      });
      if (res?.success) {
        showToast("Team percentages saved!", "success", 2000);
        setIsEditingTeam(false);
        setOriginalTeamData(null);
      } else {
        showToast("Failed to save team", "error");
      }
    } catch (err) {
      console.error("Error saving team:", err);
      showToast("Error saving team", "error");
    } finally {
      setSavingTeam(false);
    }
  };

  const handleCancelTeamEdit = () => {
    if (originalTeamData) {
      setPlaceData(prev => ({ ...prev, team: originalTeamData }));
    }
    setIsEditingTeam(false);
    setOriginalTeamData(null);
  };

  const handleStartTeamEdit = () => {
    setOriginalTeamData(placeData.team);
    setIsEditingTeam(true);
  };

  // Fetch module defaults and auto-prefetch warranty period
  useEffect(() => {
    const fetchDefaults = async () => {
      const isIT = Number(placeData?.placementType) === 1;
      const isBench = placementData?.module === "BenchSales" || placementData?.module === "Bench";
      const settingsKey = isBench ? "INCENTIVE_BENCH" : (isIT ? "INCENTIVE_IT" : "INCENTIVE_NON_IT");
      
      try {
        const response = await fetchIncentiveSettingsApi(settingsKey).catch(() => ({}));
        
        // Robustly extract the settings document
        const settingsDoc = response?.data?.[0] || response?.data || response || {};
        const settingsValue = settingsDoc?.value || {};
        
        // Auto-fetch warranty period (decoupled from ATS)
        const warrantyField = isIT ? "minimumStayPeriod" : "probationPeriod";
        const rawWarranty = isIT 
          ? settingsValue?.itSettingDetails?.[0]?.[warrantyField] 
          : settingsValue?.nonItSettingDetails?.[0]?.[warrantyField];
        
        const mappedW = mapWarrantyPeriod(rawWarranty);
        
        if (mappedW) {
          setPlaceData(prev => ({ ...prev, warrantyPeriod: mappedW }));
        }
      } catch (err) {
        console.error("Error fetching incentive settings for warranty:", err);
      }
    };

    if (!isProcessed) {
        fetchDefaults();
    }
  }, [placeData?.placementType, isProcessed]);

  // Fetch member-specific incentives for assigned team
  useEffect(() => {
    const fetchTeamIncentives = async () => {
      const team = placeData?.team || [];
      const uninitializedMembers = team.filter(m => m.userId && !m.incentiveRates);
      
      if (uninitializedMembers.length === 0) return;

      try {
        setLoadingIncentives(true);
        // Derive module from placementType: 1-Contract(IT), 2-Permanent(NonIT), respecting Bench override
        const isBench = placementData?.module === "BenchSales" || placementData?.module === "Bench";
        const derivedModule = isBench ? placementData.module : (Number(placeData?.placementType) === 1 ? "IT" : "NonIT");
        const settingsKey = isBench ? "INCENTIVE_BENCH" : (derivedModule === "IT" ? "INCENTIVE_IT" : "INCENTIVE_NON_IT");
        
        const [moduleDefaults] = await Promise.all([
          fetchIncentiveSettings(settingsKey).catch(() => ({})),
        ]);

        const defaultRates = {
          generalPct: Number(moduleDefaults?.generalPct) || 0,
          q1Pct: Number(moduleDefaults?.q1Pct) || 0,
          fiscalYearPct: Number(moduleDefaults?.fiscalYearPct) || 0,
          fixedAmount: 0
        };

        const updatedTeam = await Promise.all(team.map(async member => {
          if (!member.userId || member.incentiveRates) return member;

          try {
            // Fetch user's global incentive card for this module
            const response = await fetchIncentives({
              filters: { 
                targetUserId: { type: "NONE", value: member.userId }, 
                module: { type: "NONE", value: derivedModule } 
              },
              limit: 1
            }).catch(() => null);

            const userIncentives = response?.data?.[0];

            const rates = userIncentives ? {
                generalPct: userIncentives.generalPct || 0,
                q1Pct: userIncentives.q1Pct || 0,
                fiscalYearPct: userIncentives.fiscalYearPct || 0,
                fixedAmount: userIncentives.fixedAmount || 0
            } : defaultRates;

            return {
              ...member,
              incentiveRates: rates,
              percentage: member.percentage || rates.generalPct
            };
          } catch (err) {
            return { ...member, incentiveRates: defaultRates, percentage: member.percentage || defaultRates.generalPct };
          }
        }));

        setPlaceData(prev => ({ ...prev, team: updatedTeam }));
      } catch (err) {
        console.error("Error fetching team incentives:", err);
      } finally {
        setLoadingIncentives(false);
      }
    };

    if (!isProcessed) {
        fetchTeamIncentives();
    }
  }, [placeData?.team?.length, isProcessed, placeData?.placementType]);

  const handleFieldChange = (setter) => (key, value) => {
    setter((prev) => {
      let updated = setNestedValue(prev, key, value);

      // Special logic for placeData to auto-calculate fields (Synced with EditPlacementDetails.jsx)
      if (setter === setPlaceData) {
        const parseNumLocal = (v) => {
          if (typeof v === "number") return v;
          if (!v || v === "-") return 0;
          if (typeof v !== "string") return 0;
          return Number(v.replace(/[$,%]/g, "")) || 0;
        };

        const isPermanent = Number(updated.placementType) === 2;
        const isPercentage = Number(updated.commissionType) === 2;

        // 1. Handle placementType switch to preserve profit/fee integrity
        if (key === "placementType") {
          const oldTarget = parseNumLocal(prev.targetAmount);
          const oldBill = parseNumLocal(prev.billRate);
          const oldSalary = parseNumLocal(prev.offeredSalary) || oldBill;

          if (isPermanent) {
            updated.offeredSalary = oldBill;
            updated.billRate = oldBill;
          } else {
            updated.billRate = oldSalary;
            updated.payRate = Math.max(0, oldSalary - oldTarget);
          }
        }

        const bill = parseNumLocal(updated.billRate);
        const targetPct = parseNumLocal(updated.targetPercentage);
        const targetAmt = parseNumLocal(updated.targetAmount);

        // 2. Main Calculation Engine
        if (!isPermanent) {
          // Contract (IT) Logic: Target Amount = Bill Rate - Pay Rate
          const pay = parseNumLocal(updated.payRate);
          updated.offeredSalary = pay;
          updated.targetAmount = bill - pay;
          updated.targetPercentage = bill > 0 ? Math.round(((bill - pay) / bill) * 100 * 100) / 100 : 0;
        } else {
          // Permanent (Non-IT) Logic: Bill Rate always equals Offered Salary
          if (key === "offeredSalary") {
            updated.billRate = updated.offeredSalary;
          } else if (key === "billRate") {
            updated.offeredSalary = updated.billRate;
          } else {
            updated.billRate = updated.offeredSalary;
          }

          const baseForCalc = parseNumLocal(updated.offeredSalary);

          if (isPercentage) {
            // Percentage: Target Amount = Salary * (Percentage / 100)
            updated.targetAmount = Math.round(baseForCalc * (targetPct / 100));
          } else {
            // Flat: Percentage = Target Amount / Salary
            updated.targetPercentage = baseForCalc > 0 ? Math.round((targetAmt / baseForCalc) * 100 * 100) / 100 : 0;
          }
        }
      }

      return updated;
    });
  };

  /* ─── Save Current State to Backend ─── */
  const saveCurrentState = async () => {
    try {
      const isBench = placementData?.module === "BenchSales" || placementData?.module === "Bench";
      const effectiveModule = isBench ? placementData.module : (placeData?.placementType === 1 ? "IT" : "NonIT");

      await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.INCOMING_PLACEMENTS.UPDATE,
        params: { coll: "IncomingPlacement", uiid: placementData.uiid },
        data: { 
          payload: { 
            updateFields: { 
              type: "SET", 
              value: { 
                employee: empData, 
                client: clientData, 
                placement: placeData,
                module: effectiveModule,
                _skipFlatten: ["employee", "client", "placement"] 
              } 
            } 
          } 
        },
      });
    } catch (err) {
      console.error("Error saving current state:", err);
    }
  };

  /* ── Navigation Handlers ── */
  const handleCreateEmployee = async () => {
    await saveCurrentState();
    navigate("/employee/add-employee", {
      state: {
        incomingData: {
          incomingPlacementId: placementData?.uiid || null,
          firstName: empData?.firstName || "",
          middleName: empData?.middleName || "",
          lastName: empData?.lastName || "",
          emailAddress: empData?.emailAddress || "",
          alternateEmailAddress: empData?.alternateEmailAddress || "",
          contactNumber: typeof empData?.contactNumber === "object" ? empData.contactNumber.number : (empData?.contactNumber || ""),
          countryCode: typeof empData?.contactNumber === "object" ? empData.contactNumber.code : "+1",
          jobTitle: empData?.jobTitle || "",
          fullAddress: empData?.address?.line1 || "",
          zipCode: empData?.address?.zipCode || "",
          dateOfJoining: (() => {
            const rawDate = empData?.startDate || placeData?.startDate;
            if (!rawDate || rawDate === "-") return "";
            if (/^\d{2}-\d{2}-\d{4}$/.test(rawDate)) {
                return `${rawDate.split('-')[2]}-${rawDate.split('-')[1]}-${rawDate.split('-')[0]}`;
            }
            return rawDate;
          })(),
          dateOfBirth: (() => {
            const rawDate = empData?.dob;
            if (!rawDate || rawDate === "-") return "";
            if (/^\d{2}-\d{2}-\d{4}$/.test(rawDate)) {
                return `${rawDate.split('-')[2]}-${rawDate.split('-')[1]}-${rawDate.split('-')[0]}`;
            }
            return rawDate;
          })(),
          gender: empData?.gender ? Number(empData.gender) : undefined,
          employmentType: empData?.employmentType ? Number(empData.employmentType) : undefined,
          classification: Number(empData?.classification) || 1,
          countryList: empData?.address?.country
            ? { label: empData.address.country, value: empData.address.country }
            : "",
          state: empData?.address?.state
            ? { label: empData.address.state, value: empData.address.state }
            : "",
          city: empData?.address?.city
            ? { label: empData.address.city, value: empData.address.city }
            : "",
        },
      },
    });
  };

  const handleCreateClient = async () => {
    await saveCurrentState();
    // Build email list from client-level + POC emails
    const emailList = [];
    if (clientData?.email) emailList.push(clientData.email);
    if (clientData?.poc?.email && !emailList.includes(clientData.poc.email)) emailList.push(clientData.poc.email);

    // Build phone list from client-level + POC phones
    const phoneList = [];
    if (clientData?.contactNumber) {
      const p = clientData.contactNumber;
      phoneList.push({ code: p.code || p.countryCode || "+1", number: p.number || (typeof p === "string" ? p : "") });
    }
    if (clientData?.poc?.phone) {
      const p = clientData.poc.phone;
      phoneList.push({ code: p.code || p.countryCode || "+1", number: p.number || (typeof p === "string" ? p : "") });
    }

    // Build roleContacts from POC + billingPOC
    const roleContacts = [];
    if (clientData?.poc?.name) {
      roleContacts.push({
        id: uuidv4(),
        name: clientData.poc.name,
        type: "",
        email: [clientData.poc.email].filter(Boolean),
        mobileNumber: clientData.poc.phone ? [{ code: clientData.poc.phone.code || "+1", number: clientData.poc.phone.number || (typeof clientData.poc.phone === "string" ? clientData.poc.phone : "") }] : [],
      });
    }
    if (clientData?.billingPOC?.name) {
      roleContacts.push({
        id: uuidv4(),
        name: clientData.billingPOC.name,
        type: "Billing",
        email: [clientData.billingPOC.email].filter(Boolean),
        mobileNumber: clientData.billingPOC.phone ? [{ code: clientData.billingPOC.phone.code || "+1", number: clientData.billingPOC.phone.number || (typeof clientData.billingPOC.phone === "string" ? clientData.billingPOC.phone : "") }] : [],
      });
    }

    navigate("/client/add-client", {
      state: {
        incomingData: {
          incomingPlacementId: placementData?.uiid || null,
          businessName: clientData?.legalBusinessName || "",
          email: emailList,
          federalId: clientData?.federalId || "",
          contactNumber: phoneList.length > 0 ? phoneList : [],
          website: clientData?.website || "",
          name: roleContacts.length > 0 ? roleContacts : [],
        },
      },
    });
  };

  const handleCreatePlacement = async () => {
    if (!placementData?.wfEmployeeUiid) {
      showToast("Employee must be created in WorkForce before adding a placement.", "error", 5000);
      return;
    }
    if (!placementData?.wfClientUiid) {
      showToast("Client must be created in WorkForce before adding a placement.", "error", 5000);
      return;
    }

    await saveCurrentState();

    // Determine route based on placementType (1 = Contract/IT, 2 = Permanent/NonIT)
    // Use placementType as primary source, fall back to module field
    const placementTypeNum = Number(placeData?.placementType);
    const isPermanent = placementTypeNum
      ? placementTypeNum === 2
      : placementData?.module === "NonIT";
    const targetUrl = isPermanent
      ? `/employee/employee-profile?profile-info=placements&placements=add-permanent-placement`
      : `/employee/employee-profile?profile-info=placements&placements=add-client-placement`;

    // Ensure Redux knows which Employee Profile to open and clear stale placement data
    dispatch(setEmployeeProfileUiid(placementData.wfEmployeeUiid));
    dispatch(addCurrentPlacement({}));

    navigate(targetUrl, {
      state: {
        incomingData: {
          incomingPlacementId: placementData?.uiid || null,
          billableClient: placementData?.wfClientUiid ? { label: clientData?.legalBusinessName, value: placementData.wfClientUiid } : "",
          jobCode: placeData?.jobCode || "",
          jobTitle: placeData?.jobTitle || "",
          startDate: placeData?.startDate ? (
             /^\d{2}-\d{2}-\d{4}$/.test(placeData.startDate) ? `${placeData.startDate.split('-')[2]}-${placeData.startDate.split('-')[1]}-${placeData.startDate.split('-')[0]}` : 
             /^\d{4}-\d{2}-\d{2}$/.test(placeData.startDate) ? placeData.startDate : placeData.startDate
          ) : "",
          endDate: placeData?.endDate ? (
             /^\d{2}-\d{2}-\d{4}$/.test(placeData.endDate) ? `${placeData.endDate.split('-')[2]}-${placeData.endDate.split('-')[1]}-${placeData.endDate.split('-')[0]}` : 
             /^\d{4}-\d{2}-\d{2}$/.test(placeData.endDate) ? placeData.endDate : placeData.endDate
          ) : "",
          payRate: placeData?.payRate || "",
          billRate: placeData?.billRate || "",
          billingType: placeData?.billType || "",
          offeredSalary: placeData?.offeredSalary || "",
          targetPercentage: placeData?.targetPercentage || "",
          targetAmount: placeData?.targetAmount || "",
          warrantyPeriod: placeData?.warrantyPeriod || "",
          placementType: placementTypeNum || (isPermanent ? 2 : 1),
          commissionType: placeData?.commissionType || 2,
          team: placeData?.team || [],
        },
      },
    });
  };

  /* ── Approve Incoming Placement ── */
  const handleApprovePlacement = async () => {
    if (!placementData?.wfEmployeeUiid || !placementData?.wfClientUiid || !placementData?.wfPlacementUiid) {
      showToast("Employee, Client, and Placement must all be created in WorkForce before approving.", "error", 5000);
      return;
    }

    try {
      setApproving(true);
      const isBench = placementData?.module === "BenchSales" || placementData?.module === "Bench";
      const effectiveModule = isBench ? placementData.module : (placeData?.placementType === 1 ? "IT" : "NonIT");

      const res = await axiosRequest({
        method: "POST",
        url: `${ENDPOINTS.INCOMING_PLACEMENTS.APPROVE}/${placementData.uiid}/approve`,
        data: {
          team: placeData.team,
          placementId: placementData.wfPlacementUiid,
          module: effectiveModule,
        },
      });

      if (res?.success) {
        showToast("Placement approved and incentive records created!", "success");
        onBack();
      } else {
        showToast(res?.msg || "Failed to approve placement", "error");
      }
    } catch (error) {
      console.error("Error approving placement:", error);
      showToast("Error approving placement", "error");
    } finally {
      setApproving(false);
    }
  };

  /* ─── Render a read-only section (rows of 4 fields) ─── */
  const renderReadOnlyFields = (fields, data) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-7 auto-rows-auto">
        {fields.map((f, fieldIdx) => (
          <div key={fieldIdx} className="col-span-1">
            <InfoText 
              title={f.label} 
              value={formatFieldValue(f.key, getNestedValue(data, f.key)) || "-"} 
              size="sm" 
              textWrap
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="px-4 py-2">
      {/* Back header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button type="icon" icon="arrow_back" variant="text" color="black" onClick={onBack} />
          <h1 className="text-xl font-semibold text-gray-800">Details</h1>
          {isProcessed && (
            <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Processed</span>
          )}
        </div>
        {!isProcessed && (
          <Button
            type="button"
            title={approving ? "Approving..." : "Approve Placement"}
            variant="contained"
            color={isReadyToApprove ? "success" : "danger"}
            size="sm"
            onClick={handleApprovePlacement}
            disabled={approving}
          />
        )}
      </div>

      {/* ─── Employee Details ─── */}
      <PlacementDetailCard
        headeRight={!isProcessed && <Button type="icon" icon="edit" variant="text" color="black" onClick={() => setEditingSection("employee")} />}
        headerLeft={
          <span className="flex items-center text-lg font-semibold text-gray-900">
            Employee Details
            {savedSections.employee && <SavedBadge />}
          </span>
        }
        className="mb-4"
      >
        {renderReadOnlyFields(EMPLOYEE_FIELDS, empData)}
      </PlacementDetailCard>

      {/* ─── Client Details ─── */}
      <PlacementDetailCard
        headeRight={!isProcessed && <Button type="icon" icon="edit" variant="text" color="black" onClick={() => setEditingSection("client")} />}
        headerLeft={
          <span className="flex items-center text-lg font-semibold text-gray-900">
            Client Details
            {savedSections.client && <SavedBadge />}
          </span>
        }
        className="mb-4"
      >
        {renderReadOnlyFields(CLIENT_FIELDS, clientData)}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <PlacementDetailCard title={<span className="text-base font-medium text-gray-800">POC Details</span>}>
            <ReadOnlyRow 
              cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              fields={[
                { label: "POC Name", value: clientData.poc?.name },
                { label: "POC Email ID", value: clientData.poc?.email },
                { label: "POC Phone Number", value: clientData.poc?.phone },
              ]} 
            />
          </PlacementDetailCard>
          <PlacementDetailCard title={<span className="text-base font-medium text-gray-800">Billing and Invoicing</span>}>
            <ReadOnlyRow 
              cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              fields={[
                { label: "Name", value: clientData.billingPOC?.name },
                { label: "Email ID", value: clientData.billingPOC?.email },
                { label: "Phone Number", value: clientData.billingPOC?.phone },
              ]} 
            />
          </PlacementDetailCard>
        </div>
      </PlacementDetailCard>

      {/* ─── Placement Details ─── */}
      <PlacementDetailCard
        title={
          <span className="text-lg font-semibold text-gray-900 flex items-center">
            Placement Details
            {savedSections.placement && <SavedBadge />}
          </span>
        }
        headeRight={
          <div className="flex items-center gap-2">
            {!isProcessed && (
              <Button type="icon" icon="edit" variant="text" color="black" onClick={() => setEditingSection("placement")} />
            )}
          </div>
        }
        className="mb-4"
      >
        {renderReadOnlyFields(PLACEMENT_FIELDS.filter(f => {
          const isPermanent = placeData?.placementType === 2;
          const isPct = placeData?.commissionType === 2;
          if (isPermanent) {
            if (f.key === "billRate" || f.key === "payRate") return false;
            if (f.key === "targetPercentage") return isPct;
            return true;
          }
          return !["offeredSalary", "commissionType"].includes(f.key);
        }), placeData)}
        {placeData.team?.length > 0 && (
          <>
            <hr className="my-5 border-gray-200" />
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-semibold text-gray-800">Team</h3>
              {!isProcessed && (
                <div className="flex gap-2">
                  {!isEditingTeam ? (
                    <Button 
                      type="icon" 
                      icon="edit" 
                      variant="text" 
                      color="black" 
                      onClick={handleStartTeamEdit}
                    />
                  ) : (
                    <>
                      <Button 
                        title="Cancel" 
                        label="Cancel"
                        size="xs" 
                        variant="contained" 
                        color="secondary" 
                        onClick={handleCancelTeamEdit} 
                      />
                      <Button 
                        title={savingTeam ? "Saving..." : "Save Team"} 
                        label={savingTeam ? "Saving..." : "Save Team"} 
                        size="xs" 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSaveTeam}
                        disabled={savingTeam}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {placeData.team.map((m, idx) => (
                <div key={idx} className={`border rounded-xl p-4 bg-white shadow-sm flex flex-col gap-3 transition-all ${isEditingTeam ? 'border-primary ring-1 ring-primary/10' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2 mb-1">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{m.role}</span>
                      <div className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">{m.name || "Unassigned"}</div>
                    </div>
                    {!isProcessed && m.incentiveRates && (
                      <div className="bg-primary/5 px-2 py-0.5 rounded text-[10px] font-medium text-primary">
                        Default: {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? `$${m.incentiveRates.fixedAmount || 0}` : `${m.incentiveRates.generalPct}%`}
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-gray-50 rounded p-1.5 text-center">
                      <div className="text-[9px] text-gray-500 font-medium leading-tight">General</div>
                      <div className="text-xs font-bold text-gray-800">{placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "0%" : `${m.incentiveRates?.generalPct ?? m.percentage ?? 0}%`}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-1.5 text-center">
                      <div className="text-[9px] text-gray-500 font-medium leading-tight">Q1</div>
                      <div className="text-xs font-bold text-gray-800">{placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "0%" : `${m.incentiveRates?.q1Pct ?? 0}%`}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-1.5 text-center">
                      <div className="text-[9px] text-gray-500 font-medium leading-tight">Yearly</div>
                      <div className="text-xs font-bold text-gray-800">{placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "0%" : `${m.incentiveRates?.fiscalYearPct ?? 0}%`}</div>
                    </div>
                  </div>

                  {!isProcessed && isEditingTeam && m.userId ? (
                    <div className="mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                      <label className="text-[10px] text-gray-500 font-semibold block mb-1">
                        {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "Incentive ($)" : "Commission (%)"}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                          {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "$" : ""}
                        </span>
                        <input
                          type="number"
                          className={`w-full h-9 ${placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "pl-6 pr-3" : "pl-3 pr-8"} text-sm border border-primary/30 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-medium bg-white`}
                          value={placementData?.module === "Bench" || placementData?.module === "BenchSales" ? (m.incentiveRates?.fixedAmount ?? "") : (m.percentage ?? "")}
                          onChange={(e) => {
                            const val = e.target.value === "" ? "" : Number(e.target.value);
                            const newTeam = [...placeData.team];
                            if (placementData?.module === "Bench" || placementData?.module === "BenchSales") {
                                newTeam[idx] = { 
                                    ...newTeam[idx], 
                                    incentiveRates: { ...(newTeam[idx].incentiveRates || {}), fixedAmount: val },
                                    percentage: 0 
                                };
                            } else {
                                newTeam[idx] = { ...newTeam[idx], percentage: val };
                            }
                            setPlaceData({ ...placeData, team: newTeam });
                          }}
                          autoFocus={idx === 0}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                           {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "" : "%"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    (m.percentage !== undefined || (m.incentiveRates?.fixedAmount !== undefined && (placementData?.module === "Bench" || placementData?.module === "BenchSales"))) && (
                      <div className="mt-1 flex items-center justify-between bg-primary/5 px-3 py-2 rounded-lg">
                        <span className="text-[10px] font-bold text-primary uppercase">
                            {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? "Fixed Incentive" : "Commission"}
                        </span>
                        <span className="text-sm font-bold text-primary">
                            {placementData?.module === "Bench" || placementData?.module === "BenchSales" ? `$${m.incentiveRates?.fixedAmount || 0}` : `${m.percentage}%`}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </PlacementDetailCard>

      {/* Documents */}
      {documents?.length > 0 && (
        <PlacementDetailCard title={<span className="text-lg font-semibold text-gray-900">Documents</span>} className="mb-4">
          <div className="mt-2 text-sm w-full">
            <NewTableAdv 
              columns={[
                { key: "documentName", label: "Title", render: (row) => <div className="text-sm font-medium text-gray-800">{row.name || "-"}</div> },
                { key: "created", label: "Uploaded Date", render: (row) => <div className="text-sm text-gray-800">{safeDateFormat(row.createdAt || placementData.addedOn)}</div> },
                { key: "file", label: "Documents", render: (row) => (
                  <div
                    className="flex items-center gap-1 flex-row cursor-pointer"
                    onClick={() => {
                      setSelectedDocuments([{ name: row.name, filePath: row.key || row.url }]);
                      setDocumentModal(true);
                    }}
                  >
                    <span className="text-linkblue">
                      1 Docs
                    </span>
                  </div>
                )},
                { key: "actions", label: "Actions", hide: isProcessed, render: (row) => (
                  <div className="flex items-center gap-2 flex-row">
                    <Button type="icon" icon="edit" size="xs" variant="text" color="black" />
                    <Button type="icon" icon="delete" size="xs" variant="text" color="black" />
                  </div>
                )}
              ]}
              rows={mapTableRows(documents || [], [
                { key: "documentName" }, { key: "created" }, { key: "file" }, { key: "actions" }
              ])}
              tdClassName="!py-3"
            />
          </div>

          <Modal
            isOpen={documentModal}
            onClose={() => setDocumentModal(false)}
            title="Document Details"
          >
            <DocumentsList data={selectedDocuments} />
          </Modal>
        </PlacementDetailCard>
      )}

      {/* ─── Edit Modals ─── */}
      <EditSectionModal
        isOpen={editingSection === "employee"}
        onClose={() => setEditingSection(null)}
        title="Edit Employee Details"
        fields={EMPLOYEE_FIELDS}
        data={empData}
        onChange={handleFieldChange(setEmpData)}
        saveLabel="Create Employee"
        onSave={handleCreateEmployee}
      />

      <EditSectionModal
        isOpen={editingSection === "client"}
        onClose={() => setEditingSection(null)}
        title="Edit Client Details"
        fields={CLIENT_FIELDS}
        data={clientData}
        onChange={handleFieldChange(setClientData, clientData)}
        saveLabel="Create Client"
        onSave={handleCreateClient}
      >
        <div className="mt-4 flex flex-col gap-4">
          {/* POC Details Box */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-[#f9f9f9] px-4 py-2 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 text-sm">POC Details</h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white">
              <FormFieldSet
                type="text" title="POC Name" value={clientData.poc?.name || ""}
                onChange={(val) => handleFieldChange(setClientData)("poc.name", val)} size="sm" placeholder="POC Name"
              />
              <FormFieldSet
                type="text" title="POC Email ID" value={clientData.poc?.email || ""}
                onChange={(val) => handleFieldChange(setClientData)("poc.email", val)} size="sm" placeholder="POC Email ID"
              />
              <FormFieldSet
                type="phone" title="POC Phone Number" 
                code={clientData.poc?.phone?.countryCode || ""}
                value={clientData.poc?.phone?.number || ""}
                onChange={(full, details) => handleFieldChange(setClientData)("poc.phone", { countryCode: details.countryCode, number: details.number, code: details.countryCode })} 
                size="sm" placeholder="POC Phone Number"
              />
            </div>
          </div>

          {/* Billing and Invoicing Box */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-[#f9f9f9] px-4 py-2 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 text-sm">Billing and Invoicing</h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white">
              <FormFieldSet
                type="text" title="Name" value={clientData.billingPOC?.name || ""}
                onChange={(val) => handleFieldChange(setClientData)("billingPOC.name", val)} size="sm" placeholder="Name"
              />
              <FormFieldSet
                type="text" title="Email ID" value={clientData.billingPOC?.email || ""}
                onChange={(val) => handleFieldChange(setClientData)("billingPOC.email", val)} size="sm" placeholder="Email ID"
              />
              <FormFieldSet
                type="phone" title="Phone Number" 
                code={clientData.billingPOC?.phone?.countryCode || ""}
                value={clientData.billingPOC?.phone?.number || ""}
                onChange={(full, details) => handleFieldChange(setClientData)("billingPOC.phone", { countryCode: details.countryCode, number: details.number, code: details.countryCode })} 
                size="sm" placeholder="Phone Number"
              />
            </div>
          </div>
        </div>
      </EditSectionModal>

      <EditSectionModal
        isOpen={editingSection === "placement"}
        onClose={() => setEditingSection(null)}
        title="Edit Placement Details"
        fields={PLACEMENT_FIELDS.filter(f => {
          const isPermanent = placeData?.placementType === 2;
          const isPct = placeData?.commissionType === 2;
          if (isPermanent) {
            if (f.key === "billRate" || f.key === "payRate") return false;
            if (f.key === "targetPercentage") return isPct;
            return true;
          }
          return !["offeredSalary", "commissionType"].includes(f.key);
        }).map(f => {
          const isPermanent = placeData?.placementType === 2;
          const isFlat = placeData?.commissionType === 1;
          const isPct = placeData?.commissionType === 2;
          if (isPermanent) {
            if (f.key === "targetPercentage" && isFlat) return { ...f, disabled: true };
            if (f.key === "targetAmount" && isPct) return { ...f, disabled: true };
          }
          return f;
        })}
        data={placeData}
        onChange={handleFieldChange(setPlaceData)}
        saveLabel="Create Placement"
        onSave={handleCreatePlacement}
      />
    </div>
  );
};

export default IncomingPlacementDetails;
